/**
 * Save Queue Manager for Non-blocking Document Saves
 *
 * Manages background save operations to prevent blocking the UI
 * while documents are being saved to GCS, Firestore, and callback URLs.
 */

import { ReviewRequest } from "./review-request";
import { logAction, sendCriticalLog } from "./logger";

export interface SaveOperation {
  id: string;
  reviewRequest: ReviewRequest;
  accessToken: string;
  idToken: string;
  content: string;
  data: any;
  rejectReason: string;
  userId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  error?: string;
  retryCount: number;
  timestamp: Date;
  getAccessToken?: (forceRefresh?: boolean) => Promise<string>;
}

export interface SaveQueueStatus {
  pending: number;
  inProgress: number;
  completed: number;
  failed: number;
  total: number;
}

export class SaveQueue extends EventTarget {
  private queue: SaveOperation[] = [];
  private processing: boolean = false;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 2000;
  private nextOperationId = 0;

  /**
   * Add a save operation to the queue
   */
  async enqueueSave(
    reviewRequest: ReviewRequest,
    accessToken: string,
    idToken: string,
    content: string,
    data: any,
    rejectReason: string,
    userId: string,
    getAccessToken?: (forceRefresh?: boolean) => Promise<string>
  ): Promise<string> {
    const operationId = `save_${++this.nextOperationId}_${Date.now()}`;

    const operation: SaveOperation = {
      id: operationId,
      reviewRequest,
      accessToken,
      idToken,
      content,
      data,
      rejectReason,
      userId,
      status: 'pending',
      retryCount: 0,
      timestamp: new Date(),
      getAccessToken,
    };

    this.queue.push(operation);

    // Log operation enqueued
    logAction({
      timestamp: new Date().toISOString(),
      severity: 'INFO',
      userId,
      action: 'Save operation enqueued',
      additionalInfo: {
        operationId,
        reviewRequestName: reviewRequest.name,
        queueLength: this.queue.length,
      },
    });

    // Emit status change
    this.emitStatusChange();

    // Start processing if not already processing
    if (!this.processing) {
      this.processQueue();
    }

    return operationId;
  }

  /**
   * Process the queue - executes one save at a time
   */
  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      // Get next pending operation
      const operation = this.queue.find(op => op.status === 'pending');

      if (!operation) {
        // No pending operations, only failed/completed ones
        break;
      }

      await this.executeOperation(operation);
    }

    this.processing = false;

    // Emit final status
    this.emitStatusChange();
  }

  /**
   * Execute a single save operation
   */
  private async executeOperation(operation: SaveOperation): Promise<void> {
    operation.status = 'in_progress';
    this.emitStatusChange();

    try {
      // Refresh access token before save attempt (prevents expiration during queue processing)
      if (operation.getAccessToken) {
        try {
          const [freshToken, idToken] = await operation.getAccessToken(false);
          operation.accessToken = freshToken;
          console.log(`Access token refreshed for save operation ${operation.id}`);
        } catch (tokenError) {
          console.warn('Failed to refresh access token, using existing token:', tokenError);
          // Continue with existing token - if it's expired, the save will fail and retry
        }
      }

      // Log start of save
      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        userId: operation.userId,
        action: 'Save operation started',
        additionalInfo: {
          operationId: operation.id,
          reviewRequestName: operation.reviewRequest.name,
          retryCount: operation.retryCount,
        },
      });

      // Execute the actual save
      await operation.reviewRequest.saveContent(
        operation.accessToken,
        operation.idToken,
        operation.content,
        operation.data,
        operation.rejectReason
      );

      // Success!
      operation.status = 'completed';

      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        userId: operation.userId,
        action: 'Save operation completed successfully',
        additionalInfo: {
          operationId: operation.id,
          reviewRequestName: operation.reviewRequest.name,
          retryCount: operation.retryCount,
        },
      });

      // Emit completion event
      this.dispatchEvent(new CustomEvent('save-completed', {
        detail: {
          operationId: operation.id,
          reviewRequest: operation.reviewRequest,
        }
      }));

      // Auto-cleanup: Remove completed operations after a short delay
      // This prevents memory leaks from queue growing indefinitely
      setTimeout(() => {
        this.clearCompleted();
      }, 5000); // Clean up 5 seconds after completion

    } catch (error) {
      console.error('Save operation failed:', error);

      // Log the save operation failure with full details
      await sendCriticalLog({
        timestamp: new Date().toISOString(),
        severity: 'ERROR',
        userId: operation.userId,
        action: 'Save operation exception caught',
        additionalInfo: {
          operationId: operation.id,
          reviewRequestName: operation.reviewRequest.name,
          taskId: (operation.reviewRequest as any).taskId,
          queue: (operation.reviewRequest as any).queue,
          retryCount: operation.retryCount,
          maxRetries: this.MAX_RETRIES,
          willRetry: operation.retryCount < this.MAX_RETRIES,
          error: error.message || String(error),
          errorStack: error.stack || 'No stack trace available'
        },
      });

      // Check if we should retry
      if (operation.retryCount < this.MAX_RETRIES) {
        operation.retryCount++;
        operation.status = 'pending'; // Reset to pending for retry
        operation.error = error.message || String(error);

        logAction({
          timestamp: new Date().toISOString(),
          severity: 'WARNING',
          userId: operation.userId,
          action: 'Save operation failed, will retry',
          additionalInfo: {
            operationId: operation.id,
            reviewRequestName: operation.reviewRequest.name,
            retryCount: operation.retryCount,
            maxRetries: this.MAX_RETRIES,
            error: operation.error,
          },
        });

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY_MS * operation.retryCount));

      } else {
        // Max retries exceeded
        operation.status = 'failed';
        operation.error = error.message || String(error);

        // Log critical save failure
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: 'ERROR',
          userId: operation.userId,
          action: 'Save operation failed permanently - max retries exceeded',
          additionalInfo: {
            operationId: operation.id,
            reviewRequestName: operation.reviewRequest.name,
            taskId: (operation.reviewRequest as any).taskId,
            queue: (operation.reviewRequest as any).queue,
            retryCount: operation.retryCount,
            maxRetries: this.MAX_RETRIES,
            error: operation.error,
            errorStack: error.stack || 'No stack trace available'
          },
        });

        logAction({
          timestamp: new Date().toISOString(),
          severity: 'ERROR',
          userId: operation.userId,
          action: 'Save operation failed permanently',
          additionalInfo: {
            operationId: operation.id,
            reviewRequestName: operation.reviewRequest.name,
            retryCount: operation.retryCount,
            error: operation.error,
          },
        });

        // Emit failure event
        this.dispatchEvent(new CustomEvent('save-failed', {
          detail: {
            operationId: operation.id,
            reviewRequest: operation.reviewRequest,
            error: operation.error,
          }
        }));

        // Auto-cleanup: Remove failed operations after 30 seconds
        // Gives user time to see the error and manually retry if desired
        setTimeout(() => {
          this.clearCompleted();
        }, 30000); // Clean up 30 seconds after permanent failure
      }
    }

    this.emitStatusChange();
  }

  /**
   * Retry a failed operation
   */
  async retryOperation(operationId: string): Promise<void> {
    const operation = this.queue.find(op => op.id === operationId && op.status === 'failed');

    if (operation) {
      operation.status = 'pending';
      operation.retryCount = 0; // Reset retry count
      operation.error = undefined;

      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        userId: operation.userId,
        action: 'Manual retry of failed save operation',
        additionalInfo: {
          operationId: operation.id,
          reviewRequestName: operation.reviewRequest.name,
        },
      });

      this.emitStatusChange();

      if (!this.processing) {
        this.processQueue();
      }
    }
  }

  /**
   * Remove completed/failed operations from queue
   */
  clearCompleted(): void {
    const beforeCount = this.queue.length;
    this.queue = this.queue.filter(op => op.status === 'pending' || op.status === 'in_progress');
    const removed = beforeCount - this.queue.length;

    if (removed > 0) {
      console.log(`Cleared ${removed} completed/failed save operations from queue`);
      this.emitStatusChange();
    }
  }

  /**
   * Get current queue status
   */
  getStatus(): SaveQueueStatus {
    const status: SaveQueueStatus = {
      pending: 0,
      inProgress: 0,
      completed: 0,
      failed: 0,
      total: this.queue.length,
    };

    for (const op of this.queue) {
      switch (op.status) {
        case 'pending':
          status.pending++;
          break;
        case 'in_progress':
          status.inProgress++;
          break;
        case 'completed':
          status.completed++;
          break;
        case 'failed':
          status.failed++;
          break;
      }
    }

    return status;
  }

  /**
   * Check if there are any pending or in-progress operations
   */
  hasPendingOperations(): boolean {
    return this.queue.some(op => op.status === 'pending' || op.status === 'in_progress');
  }

  /**
   * Get all operations (for debugging/monitoring)
   */
  getAllOperations(): SaveOperation[] {
    return [...this.queue];
  }

  /**
   * Emit status change event
   */
  private emitStatusChange(): void {
    const status = this.getStatus();
    this.dispatchEvent(new CustomEvent('status-changed', { detail: status }));
  }
}

// Global save queue instance
export const globalSaveQueue = new SaveQueue();
